import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { LoginService, UserContext } from '../../../../services/login-service';
import { FracScheduleModel, FracScheduleReviewModel } from '../model';
import { HttpService } from '../../../utility/http-service';
import { Page } from '@syncfusion/ej2-angular-grids';
import { DateFormatterPipe } from '../../../pipes/date-formatter.pipe';
import { StatusColorPipe } from '../../../pipes/status-color.pipe';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { WeatherForecast } from '../../../../services/weather.service';
@Component({
  selector: 'app-sd-marker-popup',
  templateUrl: './marker-popup.component.html',
  styleUrls: ['./marker-popup.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DateFormatterPipe,
    StatusColorPipe
  ]
})
export class MarkerPopupComponent implements OnInit {
  @Input() well?: FracScheduleModel;
  @Input() weather?: WeatherForecast;
  @Output() viewDetails = new EventEmitter<FracScheduleModel>();
  @Output() wellChanged = new EventEmitter<FracScheduleModel>();
  @Output() backToList = new EventEmitter<void>();
  activeTab: 'operations' | 'weather' = 'operations';
  isLoaded = false;
  currentUser?: UserContext;
  lastReview?: FracScheduleReviewModel;

  // Other wells sharing this marker's location (e.g. multiple wells on one pad).
  // Populated only when there is more than one; empty otherwise.
  wellGroup: FracScheduleModel[] = [];
  // 'list' shows all wells in the group so the user can pick one; 'detail'
  // shows a single well's full info. A single-well marker always uses 'detail'.
  viewMode: 'list' | 'detail' = 'detail';

  constructor(private loginService: LoginService, private http: HttpService) {
    this.currentUser = loginService.userContext;
  }

  ngOnInit() {
    // No-op: component is created once at map init, before any well is selected.
    // loadWell() handles data population when a marker is clicked.
  }

  /**
   * Load a single well into the popup, resetting all state and fetching reviews.
   */
  loadWell(well: FracScheduleModel, weather?: WeatherForecast): void {
    this.well = well;
    this.weather = weather;
    this.wellGroup = [];
    this.viewMode = 'detail';
    this.activeTab = 'operations';
    this.lastReview = undefined;
    this.isLoaded = false;
    this.getScheduleReviews();
  }

  /**
   * Load the picker list for a set of wells sharing one marker's location.
   */
  loadGroup(wells: FracScheduleModel[]): void {
    this.well = undefined;
    this.weather = undefined;
    this.wellGroup = wells;
    this.viewMode = 'list';
    this.activeTab = 'operations';
    this.lastReview = undefined;
    this.isLoaded = false;
  }

  /**
   * Pick a well from the group's list and show its full detail.
   */
  selectFromList(well: FracScheduleModel): void {
    this.well = well;
    this.viewMode = 'detail';
    this.activeTab = 'operations';
    this.lastReview = undefined;
    this.isLoaded = false;
    this.getScheduleReviews();
    this.wellChanged.emit(well);
  }

  /**
   * Return to the group's picker list from a well's detail view.
   */
  onBackToList(): void {
    this.viewMode = 'list';
    this.backToList.emit();
  }

  /**
   * Index of the currently shown well within its group, or -1 outside a group.
   */
  getGroupIndex(): number {
    if (!this.well || this.wellGroup.length <= 1) return -1;
    return this.wellGroup.findIndex(w => w.wellId === this.well!.wellId);
  }

  /**
   * Cycle to the previous (-1) or next (1) well in the group, wrapping around.
   */
  cycleGroupWell(delta: number): void {
    const idx = this.getGroupIndex();
    if (idx === -1) return;
    const nextIdx = (idx + delta + this.wellGroup.length) % this.wellGroup.length;
    this.selectFromList(this.wellGroup[nextIdx]);
  }

  /**
   * Update weather data on an already-open popup.
   */
  updateWeather(weather?: WeatherForecast): void {
    this.weather = weather;
    if (!weather && this.activeTab === 'weather') {
      this.activeTab = 'operations';
    }
  }

  /**
   * Clear all state to prevent stale data flash on reopen.
   */
  clear(): void {
    this.well = undefined;
    this.weather = undefined;
    this.wellGroup = [];
    this.viewMode = 'detail';
    this.lastReview = undefined;
    this.isLoaded = false;
    this.activeTab = 'operations';
  }

  getStatusText(well: FracScheduleModel | undefined = this.well) {
    if (well?.startIn != null && well.duration != null) {
      if (well.startIn < 0 && -well.startIn >= well.duration) {
        return 'Finished';
      }
      if (well.startIn < 0) {
        return 'Operating';
      }
    }

    if (well?.startIn === 0) {
      return 'Started';
    }
    if (well?.startIn === 1) {
      return 'Starting in 1 day';
    }
    if (well?.startIn && well.startIn > 1) {
      return `Starting in ${well.startIn} days`;
    }

    return 'Unknown';
  }

  /**
   * gets the status class for risk level
   * @param riskLevel the risk level
   */
  getStatusClass(riskLevel?:string) {
    let levelClass = '';
    if (riskLevel === 'High') {
      levelClass = 'red-box';
    }
    if (riskLevel === 'Medium') {
      levelClass = 'yellow-box';
    }
    if (riskLevel === 'Low') {
      levelClass = 'green-box';
    }
    return levelClass;
  }

  getProgressText() {
    if (this.well?.startIn && this.well.startIn < 0) {
      return '';
    }
    if (!this.well?.startIn || this.well.startIn === 0)
      return "0%";
    else {
      if (this.well.startIn <= 0)
      {
        var pct = (-this.well.startIn / (this.well.duration ?? 1) * 100).toFixed(0) + '%';
        return `${pct} Completed - ${-this.well.startIn}/${this.well.duration} days`
      }
      else
        return `Not Started - 0/${this.well.duration} days`;
    }
  }

  /**
   * check if reviewed
   * @param well well
   */
  isReviewed(well?: FracScheduleModel) {
    if (this.lastReview)
      return true;
    return false;
  }

  /**
   * get schedule review
   */
  getScheduleReviews() {
    if (this.well && !this.isLoaded) {
      this.isLoaded = true;
      this.http.get(`FracX/LastReviewByApi?companyId=${this.well.companyId}&api=${this.well.api}`, (review: FracScheduleReviewModel) => {
        if (review.reviewDate || review.reviewerName || review.riskLevel) {
          this.lastReview = review;
        }
      }, false);
    }
  }

  onViewDetails(): void {
    if (this.well) {
      this.viewDetails.emit(this.well);
    }
  }

  getDaysText(well: FracScheduleModel | undefined = this.well): string {
    if (well?.startIn != null) {
      if (well.startIn < 0 && well.duration != null && -well.startIn >= well.duration) {
        return 'Finished';
      }
      if (well.startIn <= 0) return 'Operating';
      return well.startIn.toString();
    }
    return '-';
  }

  getWeatherIcon(icon: string): string {
    return `https://openweathermap.org/img/wn/${icon}@2x.png`;
  }

  getWeatherRiskColor(risk: string): string {
    const colors: any = {
      low: '#04AC66',
      medium: '#ffae00',
      high: '#ff6600',
      critical: '#e63946'
    };
    return colors[risk] || '#6e6e6e';
  }

  getWeatherRiskLabel(risk: string): string {
    const labels: any = {
      low: 'Favorable',
      medium: 'Monitor',
      high: 'Challenging',
      critical: 'At Risk'
    };
    return labels[risk] || 'Unknown';
  }

  getWeatherRiskClass(risk: string): string {
    const classes: any = {
      low: 'green-box',
      medium: 'yellow-box',
      high: 'red-box',
      critical: 'red-box'
    };
    return classes[risk] || '';
  }

}
